import { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "@/lib/toast";
import { Shield, Lock, User, LogIn, KeyRound, ArrowLeft, Fingerprint, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";

const BACKEND_URL = window.BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function base64urlToBuffer(base64url) {
  const base64 = base64url.replace(/-/g, "+").replace(/_/g, "/");
  const pad = base64.length % 4 === 0 ? "" : "=".repeat(4 - (base64.length % 4));
  const binary = atob(base64 + pad);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function AdminLogin({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState("login");
  const [pendingToken, setPendingToken] = useState("");
  const [twoFACode, setTwoFACode] = useState("");
  const [rememberDevice, setRememberDevice] = useState(false);
  const [hasPasskeys, setHasPasskeys] = useState(false);
  const [isPasskeyLoading, setIsPasskeyLoading] = useState(false);
  const codeInputRef = useRef(null);

  const checkPasskeys = useCallback(async () => {
    if (!window.PublicKeyCredential) return;
    try {
      const res = await axios.get(`${API}/admin/passkeys/available`);
      setHasPasskeys(res.data.available);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { checkPasskeys(); }, [checkPasskeys]);

  const loginWithPasskey = async () => {
    setIsPasskeyLoading(true);
    try {
      const optRes = await axios.post(`${API}/admin/passkeys/auth-options`);
      const options = JSON.parse(optRes.data.options);
      const authId = optRes.data.auth_id;

      options.challenge = base64urlToBuffer(options.challenge);
      if (options.allowCredentials) {
        options.allowCredentials = options.allowCredentials.map(c => ({
          ...c, id: base64urlToBuffer(c.id)
        }));
      }

      const credential = await navigator.credentials.get({ publicKey: options });

      const credentialJSON = {
        id: credential.id,
        rawId: bufferToBase64url(credential.rawId),
        type: credential.type,
        response: {
          authenticatorData: bufferToBase64url(credential.response.authenticatorData),
          clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
          signature: bufferToBase64url(credential.response.signature),
          userHandle: credential.response.userHandle ? bufferToBase64url(credential.response.userHandle) : null,
        },
      };

      const res = await axios.post(`${API}/admin/passkeys/auth-verify`, {
        credential: credentialJSON,
        auth_id: authId,
      });

      localStorage.setItem("admin_token", res.data.token);
      localStorage.setItem("admin_expires", res.data.expires_at);
      toast.success("Connexion réussie");
      onLogin();
    } catch (err) {
      if (err.name === "NotAllowedError") {
        // User cancelled
      } else {
        toast.error(err.response?.data?.detail || "Échec de l'authentification");
      }
    } finally {
      setIsPasskeyLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const deviceToken = localStorage.getItem("device_trust_token");
      const response = await axios.post(`${API}/admin/login`, {
        username,
        password,
        device_token: deviceToken || undefined,
      });

      if (response.data.requires_2fa) {
        setPendingToken(response.data.pending_token);
        setStep("2fa");
        setTwoFACode("");
        setTimeout(() => codeInputRef.current?.focus(), 100);
      } else {
        localStorage.setItem("admin_token", response.data.token);
        localStorage.setItem("admin_expires", response.data.expires_at);
        toast.success("Connexion réussie");
        onLogin();
      }
    } catch (error) {
      const detail = error.response?.data?.detail || "Identifiants invalides";
      if (error.response?.status === 429) {
        toast.error("Compte bloqué", { description: detail });
      } else {
        toast.error("Échec de la connexion", { description: detail });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    if (!twoFACode.trim()) return;
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/admin/2fa/verify-login`, {
        code: twoFACode,
        pending_token: pendingToken,
        remember_device: rememberDevice,
      });

      localStorage.setItem("admin_token", response.data.token);
      localStorage.setItem("admin_expires", response.data.expires_at);

      if (response.data.device_token) {
        localStorage.setItem("device_trust_token", response.data.device_token);
      }

      if (response.data.used_recovery_code) {
        toast.warning("Code de récupération utilisé", {
          description: "Pensez à régénérer vos codes dans les paramètres.",
        });
      } else {
        toast.success("Connexion réussie");
      }
      onLogin();
    } catch (error) {
      toast.error("Vérification échouée", {
        description: error.response?.data?.detail || "Code invalide",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const goBackToLogin = () => {
    setStep("login");
    setPendingToken("");
    setTwoFACode("");
  };

  return (
    <div className="min-h-screen bg-admin-gradient flex items-center justify-center p-4 relative overflow-hidden">
      {/* Ambient glow effects */}
      <div className="absolute top-1/4 -left-32 w-64 h-64 bg-amber-500/5 rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute bottom-1/4 -right-32 w-64 h-64 bg-amber-500/5 rounded-full blur-[100px] pointer-events-none" />

      <div className="w-full max-w-sm relative z-10">
        {/* Logo & branding */}
        <div className="text-center mb-8">
          <div className="relative inline-flex">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-amber-500/20 via-amber-600/15 to-orange-500/10 flex items-center justify-center mx-auto mb-5 border border-amber-500/20 backdrop-blur-sm shadow-lg shadow-amber-500/5">
              {step === "2fa" ? (
                <KeyRound className="w-9 h-9 text-amber-400" />
              ) : (
                <Shield className="w-9 h-9 text-amber-400" />
              )}
            </div>
            <div className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 rounded-full border-2 border-zinc-950 animate-pulse" />
          </div>
          <h1 className="text-2xl font-mono font-bold text-white tracking-tight">
            {step === "2fa" ? "Vérification 2FA" : "Admin Panel"}
          </h1>
          <div className="flex items-center justify-center gap-2 mt-2">
            <ShieldCheck className="w-3.5 h-3.5 text-amber-500/60" />
            <p className="text-zinc-500 text-sm">
              {step === "2fa"
                ? "Entrez le code de votre application d'authentification"
                : "Accès sécurisé au panneau d'administration"}
            </p>
          </div>
        </div>

        {/* Card */}
        <div className="bg-zinc-900/60 backdrop-blur-xl border border-zinc-800/80 rounded-2xl p-6 shadow-2xl shadow-black/40">
          {step === "login" ? (
            <div className="space-y-5">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-zinc-400 mb-1.5 block font-medium">
                      Nom d'utilisateur
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <Input
                        type="text"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        placeholder="admin"
                        className="pl-10 bg-zinc-800/50 border-zinc-700/50 text-white h-12 font-mono focus:border-amber-500/50 focus:ring-amber-500/20 rounded-xl transition-colors"
                        required
                        autoFocus
                        autoComplete="username"
                        data-testid="admin-login-username"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs text-zinc-400 mb-1.5 block font-medium">
                      Mot de passe
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <Input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        className="pl-10 bg-zinc-800/50 border-zinc-700/50 text-white h-12 font-mono focus:border-amber-500/50 focus:ring-amber-500/20 rounded-xl transition-colors"
                        required
                        autoComplete="current-password"
                        data-testid="admin-login-password"
                      />
                    </div>
                  </div>
                </div>

                <label className="flex items-center gap-2.5 cursor-pointer group">
                  <Checkbox
                    id="remember-login"
                    checked={rememberDevice}
                    onCheckedChange={setRememberDevice}
                    className="border-zinc-600 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                    data-testid="remember-device-login-checkbox"
                  />
                  <span className="text-xs text-zinc-400 group-hover:text-white transition-colors select-none">
                    Se souvenir de moi
                  </span>
                </label>

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-black font-mono font-semibold h-12 rounded-xl shadow-lg shadow-amber-500/20 transition-colors hover:shadow-amber-500/30 hover:scale-[1.01] active:scale-[0.99]"
                  data-testid="admin-login-submit"
                >
                  {isLoading ? (
                    <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin mr-2" />
                  ) : (
                    <LogIn className="w-4 h-4 mr-2" />
                  )}
                  Connexion
                </Button>
              </form>

              {hasPasskeys && (
                <>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-px bg-gradient-to-r from-transparent via-zinc-700 to-transparent" />
                    <span className="text-[10px] text-zinc-600 uppercase tracking-widest font-medium">ou</span>
                    <div className="flex-1 h-px bg-gradient-to-r from-transparent via-zinc-700 to-transparent" />
                  </div>
                  <Button
                    type="button"
                    onClick={loginWithPasskey}
                    disabled={isPasskeyLoading}
                    variant="outline"
                    className="w-full border-zinc-700/50 bg-zinc-800/30 text-zinc-300 hover:text-white hover:border-amber-500/40 hover:bg-amber-500/5 font-mono h-12 rounded-xl transition-colors group"
                    data-testid="passkey-login-btn"
                  >
                    {isPasskeyLoading ? (
                      <div className="w-5 h-5 border-2 border-amber-400/30 border-t-amber-400 rounded-full animate-spin mr-2.5" />
                    ) : (
                      <Fingerprint className="w-5 h-5 mr-2.5 text-amber-400 group-hover:text-amber-300 transition-colors" />
                    )}
                    Clé d'accès
                  </Button>
                </>
              )}
            </div>
          ) : (
            <form onSubmit={handleVerify2FA} className="space-y-4">
              <div>
                <label className="text-xs text-zinc-400 mb-1.5 block font-medium">
                  Code à 6 chiffres ou code de récupération
                </label>
                <Input
                  ref={codeInputRef}
                  type="text"
                  value={twoFACode}
                  onChange={(e) => setTwoFACode(e.target.value)}
                  placeholder="000000"
                  className="bg-zinc-800/50 border-zinc-700/50 text-white h-12 font-mono text-center text-lg tracking-[0.3em] focus:border-amber-500/50 focus:ring-amber-500/20 rounded-xl transition-colors"
                  maxLength={20}
                  autoComplete="one-time-code"
                  autoFocus
                  required
                  data-testid="admin-2fa-code-input"
                />
              </div>

              <label className="flex items-center gap-2.5 cursor-pointer group">
                <Checkbox
                  id="remember-device"
                  checked={rememberDevice}
                  onCheckedChange={setRememberDevice}
                  className="border-zinc-600 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                  data-testid="remember-device-checkbox"
                />
                <span className="text-xs text-zinc-400 group-hover:text-white transition-colors select-none">
                  Se souvenir de cet appareil pendant 30 jours
                </span>
              </label>

              <Button
                type="submit"
                disabled={isLoading || !twoFACode.trim()}
                className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-black font-mono font-semibold h-12 rounded-xl shadow-lg shadow-amber-500/20 transition-colors hover:shadow-amber-500/30 hover:scale-[1.01] active:scale-[0.99]"
                data-testid="admin-2fa-verify-btn"
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin mr-2" />
                ) : (
                  <Shield className="w-4 h-4 mr-2" />
                )}
                Vérifier
              </Button>

              <button
                type="button"
                onClick={goBackToLogin}
                className="w-full flex items-center justify-center gap-1.5 text-zinc-500 hover:text-amber-400 text-xs py-2 transition-colors"
                data-testid="admin-2fa-back-btn"
              >
                <ArrowLeft className="w-3 h-3" />
                Retour à la connexion
              </button>

              <p className="text-center text-zinc-600 text-[10px]">
                Vous pouvez aussi utiliser un code de récupération
              </p>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-6 space-y-2">
          <div className="flex items-center justify-center gap-2">
            <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-cyan-500/20 to-blue-600/20 border border-cyan-500/30 flex items-center justify-center overflow-hidden">
              <img src="/icons/icon-96.png" alt="" className="w-4 h-4 object-contain" onError={(e) => { e.target.style.display = "none"; }} />
            </div>
            <p className="text-xs font-mono font-semibold">
              <span className="text-zinc-600">&lt;</span>
              <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">ShadowMail</span>
              <span className="text-zinc-600"> /&gt;</span>
            </p>
          </div>
          <p className="text-zinc-500 text-[10px]">Accès réservé aux administrateurs</p>
        </div>
      </div>
    </div>
  );
}

export default AdminLogin;
